/**
 * Werte der Systemplatzhalter eines Vertrags ({{agency.*}}, {{talent.*}}, {{brand.*}}, {{deal.*}}, {{contract.*}},
 * {{dates.*}}) aus den Stammdaten. Fehlt eine Angabe, bleibt der Wert leer: die Prüfung vor dem Versand meldet ihn,
 * statt einen Ersatzwert zu erfinden. Englische Verträge bekommen die englischen Firmenangaben der Agentur
 * (Vertragseinstellungen) oder eine sichere Übertragung der deutschen (Befund 8).
 */
import {
  chosenRepresentatives, cleanRepresentatives, computeDealSplit, contractLocale, countryName, DEAL_MODEL_LABEL, deliverablesListOrText, englishLegalTerm,
  formatContractDate, formatContractMonth, formatMoney, minorStatus, noticePeriodText, normalizeCountry, representedByText, usageRightsFromPieces,
  type AgencyFee, type ContractContext, type Currency, type DeliverableLike, type Language, type Representative, type SplitLine,
} from "@talentkontor/domain";
import { PLATFORM } from "../uebersicht/labels";
import { inLang } from "../../../lib/sprache";
import { contractNumberLabel } from "./modell";

export interface OrgQuelle {
  name: string;
  legal_name: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  vat_id: string | null;
  tax_number: string | null;
  reply_to_email: string | null;
  default_commission_bp: number | null;
  agency_fee_mode?: string | null;
  agency_fee_bp?: number | null;
  agency_fee_cents?: number | null;
  locale?: string | null;
  invoice_route?: string | null;
  payment_terms_days?: number | null;
  talent_payment_terms_days?: number | null;
  brand_payment_terms_days?: number | null;
  /** Auszahlung an Creator: feste Frist (Standard) oder erst nach Zahlungseingang der Marke (0042) */
  talent_payout_mode?: string | null;
  free_revisions?: number | null;
  revision_fee_cents?: number | null;
}
export interface LegalQuelle {
  representative_name: string | null;
  representative_position: string | null;
  register_type: string | null;
  register_number: string | null;
  register_court: string | null;
  phone: string | null;
  website: string | null;
  governing_law: string | null;
  jurisdiction: string | null;
  /** 0071: alle Vertretungsberechtigten (die erste ist representative_name) */
  representatives?: readonly Representative[] | null;
}
/** Englische Fassung der Firmenangaben (Vertragseinstellungen der Agentur) */
export interface LegalEnQuelle {
  governing_law_en: string | null;
  jurisdiction_en: string | null;
  representative_position_en: string | null;
}
export interface TalentQuelle {
  first_name: string;
  last_name: string | null;
  display_name?: string | null;
  email: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  vat_id: string | null;
  tax_number: string | null;
  small_business: boolean | null;
  instagram_handle: string | null;
  commission_bp: number | null;
  /** 0042: Firma, Geburtsdatum und Erziehungsberechtigte; 0059: zweite erziehungsberechtigte Person */
  company_name?: string | null;
  birth_date?: string | null;
  guardian_name?: string | null;
  guardian2_name?: string | null;
}
export interface BrandQuelle {
  name: string;
  legal_name: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  vat_id: string | null;
  register_info?: string | null;
  payment_terms_days?: number | null;
  /** Abrechnungsweg der Marke (0039), vor dem der Agentur */
  invoice_route?: string | null;
}
export interface ContactQuelle {
  name: string | null;
  email: string | null;
}
export interface DealQuelle {
  title: string;
  model?: string | null;
  budget_cents: number | null;
  currency: string;
  commission_bp: number | null;
  start_month: string | null;
  months: number | null;
  deliverables: string | null;
  platforms: string[] | string | null;
  usage_rights: string | null;
  usage_rights_until?: string | null;
  content_deadline?: string | null;
  exclusive?: boolean | null;
  /** Exklusivität bis und für welche Branche oder Wettbewerber (0047) */
  exclusive_until?: string | null;
  exclusive_scope?: string | null;
  /** Abrechnungsweg der Kooperation (0039), vor dem der Marke und der Agentur */
  invoice_route?: string | null;
  /** an der Kooperation vereinbarte Korrekturschleifen und Vergütung je weiterer Schleife (CO-009) */
  free_revisions?: number | null;
  revision_fee_cents?: number | null;
}
export interface ItemQuelle { kind: string; description: string; amount_cents: number; commissionable: boolean }
export interface PeriodQuelle { month: string; amount_cents: number | null; status?: string }
export interface TermsQuelle {
  agency_fee_mode: string | null;
  agency_fee_bp: number | null;
  agency_fee_cents: number | null;
}
export interface KontextQuellen {
  org: OrgQuelle;
  legal: LegalQuelle | null;
  legalEn?: LegalEnQuelle | null;
  talent: TalentQuelle | null;
  brand: BrandQuelle | null;
  representedBy?: string | null;
  contact: ContactQuelle | null;
  deal: DealQuelle | null;
  items?: readonly ItemQuelle[];
  periods?: readonly PeriodQuelle[];
  /** Beiträge der Kooperation (0068) und Bezeichnungen der Plattformen aus dem Katalog der Agentur */
  beitraege?: readonly DeliverableLike[];
  plattformen?: readonly { key: string; label_de: string; label_en: string | null }[];
  terms: TermsQuelle | null;
  contract: {
    number: number | string | null; label?: string | null; title: string; date: string; kind: string | null; language: Language;
    starts_on?: string | null; ends_on?: string | null;
    /** Laufzeit und Fristen als Felder (Befund 10) */
    term_months?: number | null; auto_renew?: boolean | null; renewal_months?: number | null; notice_days?: number | null;
    agency_signatures?: number | null; exclusive_management?: boolean | null; exclusive_scope?: string | null; tail_months?: number | null;
    /** 0071: wer „vertreten durch“ für die Agentur genannt wird; ohne Wahl die ersten Vertretungen der Firmenangaben */
    agency_representatives?: readonly Representative[] | null;
  };
  /** Namen der gesetzlichen Vertretung aus den Unterzeichnern; ohne sie aus den Stammdaten des Creators */
  guardians?: readonly string[] | null;
  /** Ursprungsvertrag eines Nachtrags */
  original?: { number: string | null; title: string; date: string | null } | null;
  today: string;
}

const t = (v: string | null | undefined): string | null => (v && v.trim() ? v.trim() : null);

/** Steuerwort im Sitzland der Agentur (SAAS-12): Umsatzsteuer, in der Schweiz und Liechtenstein Mehrwertsteuer, englisch VAT. */
export function vatTerm(orgCountry: string | null | undefined, lang: Language): string {
  const c = normalizeCountry(orgCountry) ?? orgCountry;
  return lang === "en" ? "VAT" : c === "CH" || c === "LI" ? "Mehrwertsteuer" : "Umsatzsteuer";
}

/** Land als Name in der Vertragssprache (Code oder Freitext). */
export function countryLabel(code: string | null | undefined, lang: Language): string | null {
  return country(code, lang);
}

function country(code: string | null | undefined, lang: Language): string | null {
  const c = normalizeCountry(code);
  if (!c) return t(code);
  return countryName(c, lang);
}

/** Anschrift in Zeilen; null, wenn Straße oder Ort fehlen (dann meldet die Prüfung die Lücke). */
export function addressBlock(a: { street: string | null; postal_code: string | null; city: string | null; country: string | null }, lang: Language): string | null {
  const street = t(a.street);
  const city = [t(a.postal_code), t(a.city)].filter(Boolean).join(" ");
  if (!street || !t(a.city)) return null;
  return [street, city, country(a.country, lang)].filter(Boolean).join("\n");
}

function monthKey(v: string | null): string | null {
  return v && /^\d{4}-\d{2}/.test(v) ? v.slice(0, 7) : null;
}
function addMonths(key: string, n: number): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function platformText(p: DealQuelle["platforms"]): string | null {
  const list = Array.isArray(p) ? p : typeof p === "string" && p ? p.replace(/^\{|\}$/g, "").split(",") : [];
  const names = list.map((x) => x.trim()).filter(Boolean).map((x) => inLang("de", () => PLATFORM[x]) ?? x);
  return names.length ? names.join(", ") : null;
}

function agencyFee(terms: TermsQuelle | null, org: OrgQuelle): AgencyFee | null {
  const mode = terms?.agency_fee_mode ?? org.agency_fee_mode ?? "none";
  if (mode === "percent") {
    const bp = terms?.agency_fee_mode === "percent" ? terms.agency_fee_bp : org.agency_fee_bp;
    return bp ? { type: "percent", percent: bp / 100 } : null;
  }
  if (mode === "fixed") {
    const cents = terms?.agency_fee_mode === "fixed" ? terms.agency_fee_cents : org.agency_fee_cents;
    return cents ? { type: "fixed", amountCents: cents } : null;
  }
  return null;
}

const ITEM_KIND: Record<string, { de: string; en: string }> = {
  usage_rights: { de: "Nutzungsrechte", en: "Usage rights" },
  spark_ads: { de: "Spark Ads", en: "Spark Ads" },
  whitelisting: { de: "Whitelisting", en: "Whitelisting" },
  travel: { de: "Reisekosten", en: "Travel costs" },
  products: { de: "Produkte", en: "Products" },
  other: { de: "Sonstiges", en: "Other" },
};
const BARTER = new Set(["barter", "gifting"]);

const ROUTE_TEXT: Record<string, { de: string; en: string }> = {
  via_agency: { de: "Die Agentur stellt der Marke die Rechnung; der Creator rechnet mit der Agentur ab.", en: "The agency invoices the brand; the creator invoices the agency." },
  direct_to_brand: { de: "Der Creator stellt der Marke die Rechnung; die Agentur rechnet ihre Vergütung gesondert ab.", en: "The creator invoices the brand; the agency invoices its remuneration separately." },
};

/**
 * Sitz der Agentur in Deutschland (K-403d, 0302): für Klauseln, die nicht an der Rechtswahl hängen, sondern am Sitzland
 * (z. B. Künstlersozialabgabe des deutschen Verwerters). Gleichlauf mit app.contract_render_context.
 */
export function seatGermany(orgCountry: string | null | undefined): boolean {
  return (normalizeCountry(orgCountry) ?? orgCountry) === "DE";
}

/** Rechtsraum für Klauseln je Land (Befund 23): aus der Rechtswahl, sonst aus dem Sitz der Agentur; Standard Deutschland. */
export function lawCountry(governingLaw: string | null | undefined, orgCountry: string | null | undefined): "DE" | "AT" | "CH" {
  const g = (governingLaw ?? "").toLowerCase();
  if (/österreich|oesterreich|austria/.test(g)) return "AT";
  if (/schweiz|switzerland|swiss|eidgen/.test(g)) return "CH";
  if (/deutsch|german|bundesrepublik/.test(g)) return "DE";
  const c = normalizeCountry(orgCountry);
  return c === "AT" || c === "CH" ? c : "DE";
}

/** Vertretungen aus den Firmenangaben (Liste, sonst die Einzelvertretung). */
export function agencyRepresentatives(legal: Pick<LegalQuelle, "representative_name" | "representative_position" | "representatives"> | null | undefined): Representative[] {
  if (legal?.representatives?.length) return cleanRepresentatives(legal.representatives);
  return cleanRepresentatives(legal?.representative_name ? [{ name: legal.representative_name, position: legal.representative_position }] : []);
}

/** Namen aufzählen: „A“, „A und B“, „A, B und C“ (englisch „and“). */
function joinNames(names: readonly string[], lang: Language): string | null {
  const list = names.map((x) => x.trim()).filter(Boolean);
  if (!list.length) return null;
  const and = lang === "en" ? " and " : " und ";
  return list.length === 1 ? list[0]! : `${list.slice(0, -1).join(", ")}${and}${list[list.length - 1]}`;
}

/** Englische Fassung einer Firmenangabe: eigene englische Angabe, sonst sichere Übertragung, sonst der deutsche Wert. */
function inLanguage(lang: Language, de: string | null, en: string | null | undefined, kind: "governing_law" | "jurisdiction" | "position"): string | null {
  if (lang !== "en") return de;
  return t(en) ?? englishLegalTerm(kind, de) ?? de;
}

export function contractContext(q: KontextQuellen): ContractContext {
  const lang = q.contract.language;
  const locale = contractLocale(lang, q.org.locale);
  const currency = ((q.deal?.currency ?? "EUR") as Currency);
  const register = q.legal ? [t(q.legal.register_type), t(q.legal.register_number), t(q.legal.register_court)].filter(Boolean).join(" ") : "";
  const talentBp = q.talent?.commission_bp ?? q.org.default_commission_bp ?? null;
  const commissionBp = q.deal?.commission_bp ?? talentBp;
  const items = q.items ?? [];
  const barter = !!q.deal?.model && BARTER.has(q.deal.model);
  const itemLine = (i: ItemQuelle) => `${ITEM_KIND[i.kind]?.[lang] ?? i.kind}: ${i.description}${i.amount_cents ? ` (${formatMoney(i.amount_cents, currency, locale)})` : ""}`;
  const itemsText = items.length ? items.map(itemLine).join("\n") : null;
  const itemsTotal = items.reduce((a, i) => a + (i.amount_cents || 0), 0);
  // Abrechnungsweg: Kooperation vor Marke vor Agentur (wie app.deal_route)
  const route = q.deal?.invoice_route ?? q.brand?.invoice_route ?? q.org.invoice_route ?? "via_agency";
  let talentShare: number | null = null;
  let fee: number | null = null;
  if (q.deal?.budget_cents != null && commissionBp != null) {
    try {
      const lines: SplitLine[] = [{ description: q.deal.title, quantity: 1, unitPriceCents: q.deal.budget_cents }];
      for (const i of items) if (i.amount_cents > 0) lines.push({ description: i.description, quantity: 1, unitPriceCents: i.amount_cents, commissionFree: !i.commissionable });
      const split = computeDealSplit({
        lines,
        commissionPercent: commissionBp / 100,
        agencyFee: agencyFee(q.terms, q.org),
        billing: route === "direct_to_brand" ? "talent_invoices_brand" : "agency_invoices_brand",
      });
      talentShare = split.talentShare;
      fee = split.agencyFee;
    } catch {
      /* ungültige Konditionen: Werte bleiben leer, die Prüfung meldet sie */
    }
  }
  const periods = (q.periods ?? []).filter((p) => p.amount_cents != null && p.status !== "cancelled");
  const schedule = periods.length
    ? periods.map((p) => `${formatContractMonth(p.month.slice(0, 7), locale)}: ${formatMoney(p.amount_cents!, currency, locale)}`).join("\n")
    : null;
  const start = monthKey(q.deal?.start_month ?? null);
  const months = q.deal?.months ?? null;
  // Plattform der Beiträge in der Sprache des Vertrags (Katalog der Agentur, sonst die festen Bezeichnungen)
  const platformLabel = (k: string): string | null => {
    const c = q.plattformen?.find((x) => x.key === k);
    return (c ? (lang === "en" ? c.label_en?.trim() || c.label_de : c.label_de) : null) ?? inLang(lang === "en" ? "en" : "de", () => PLATFORM[k]) ?? null;
  };
  const rights = q.deal ? usageRightsFromPieces(q.beitraege ?? [], q.deal.usage_rights, { lang, platformLabel }) : null;
  const talentName = q.talent ? [q.talent.first_name, q.talent.last_name].filter((x) => x && x.trim()).join(" ") : null;
  const number = t(q.contract.label ?? null) ?? (q.contract.number != null ? contractNumberLabel(typeof q.contract.number === "number" ? q.contract.number : Number(q.contract.number)) : null);
  const law = lawCountry(q.legal?.governing_law, q.org.country);
  // Minderjährig am Tag der Unterschrift (frühestens heute, sonst Beginn bzw. Vertragsdatum), Befund 2 und VERTRAEGE-R2-01
  const minor = q.talent ? minorStatus(q.talent.birth_date ?? null, q.contract.starts_on ?? q.contract.date, q.contract.ends_on ?? null, q.today) : null;
  const guardianNames = q.guardians?.length ? joinNames(q.guardians, lang)
    : q.talent ? joinNames([q.talent.guardian_name ?? "", q.talent.guardian2_name ?? ""], lang) : null;
  const creatorPayee = q.talent ? t(q.talent.company_name ?? null) ?? (talentName || null) : null;
  const payee = route === "direct_to_brand" ? creatorPayee : t(q.org.legal_name) ?? t(q.org.name);
  const originalDate = q.original?.date ? formatContractDate(q.original.date, locale) : null;
  // 0071: Vertretung der Agentur – gewählte Personen (beim Vier-Augen-Prinzip zwei), sonst die ersten der Firmenangaben
  const orgReps = agencyRepresentatives(q.legal);
  const agencySignatures = q.contract.agency_signatures ?? 1;
  const reps = chosenRepresentatives(orgReps, q.contract.agency_representatives ?? null, agencySignatures);
  const repPosition = (p: Representative) => inLanguage(lang, t(p.position ?? null),
    p.name.toLowerCase() === (t(q.legal?.representative_name) ?? "").toLowerCase() ? q.legalEn?.representative_position_en : null, "position");
  return {
    agency: {
      legal_name: t(q.org.legal_name),
      trade_name: t(q.org.name),
      address_block: addressBlock(q.org, lang),
      street: t(q.org.street),
      postal_code: t(q.org.postal_code),
      city: t(q.org.city),
      country_name: country(q.org.country, lang),
      vat_id: t(q.org.vat_id),
      tax_number: t(q.org.tax_number),
      register_info: register || null,
      representative_name: reps[0]?.name ?? t(q.legal?.representative_name),
      representative_position: reps[0] ? repPosition(reps[0]) : inLanguage(lang, t(q.legal?.representative_position), q.legalEn?.representative_position_en, "position"),
      represented_by: representedByText(reps, lang, { fourEyes: agencySignatures > 1, position: (_p, i) => (reps[i] ? repPosition(reps[i]!) : null) }),
      email: t(q.org.reply_to_email),
      phone: t(q.legal?.phone),
      website: t(q.legal?.website),
      governing_law: inLanguage(lang, t(q.legal?.governing_law), q.legalEn?.governing_law_en, "governing_law"),
      jurisdiction: inLanguage(lang, t(q.legal?.jurisdiction), q.legalEn?.jurisdiction_en, "jurisdiction"),
      default_commission_percent: q.org.default_commission_bp != null ? q.org.default_commission_bp / 100 : null,
      payout_days: q.org.talent_payment_terms_days ?? q.org.payment_terms_days ?? null,
      payment_terms_days: q.brand?.payment_terms_days ?? q.org.brand_payment_terms_days ?? q.org.payment_terms_days ?? null,
      invoice_route: ROUTE_TEXT[route]?.[lang] ?? null,
      invoices_brand: route !== "direct_to_brand",
      // SAAS-6: Auszahlungsklausel im Rahmenvertrag folgt der Einstellung (leer = Plattform-Standard feste Frist)
      payout_after_brand_payment: q.org.talent_payout_mode === "after_brand_payment",
      vat_term: vatTerm(q.org.country, lang),
      law_de: law === "DE",
      law_at: law === "AT",
      law_ch: law === "CH",
      seat_de: seatGermany(q.org.country),
    },
    talent: q.talent
      ? {
          full_name: talentName || null,
          first_name: t(q.talent.first_name),
          last_name: t(q.talent.last_name),
          address_block: addressBlock(q.talent, lang),
          street: t(q.talent.street),
          postal_code: t(q.talent.postal_code),
          city: t(q.talent.city),
          country_name: country(q.talent.country, lang),
          email: t(q.talent.email),
          vat_id: t(q.talent.vat_id),
          tax_number: t(q.talent.tax_number),
          small_business: q.talent.small_business,
          instagram_handle: q.talent.instagram_handle ? `@${q.talent.instagram_handle.replace(/^@/, "")}` : null,
          commission_percent: talentBp != null ? talentBp / 100 : null,
          company_name: t(q.talent.company_name ?? null),
          birth_date: q.talent.birth_date ?? null,
          is_minor: minor?.minor ?? false,
          guardian_names: guardianNames,
        }
      : {},
    brand: q.brand
      ? {
          legal_name: t(q.brand.legal_name) ?? t(q.brand.name),
          name: t(q.brand.name),
          address_block: addressBlock(q.brand, lang),
          street: t(q.brand.street),
          postal_code: t(q.brand.postal_code),
          city: t(q.brand.city),
          country_name: country(q.brand.country, lang),
          vat_id: t(q.brand.vat_id),
          register_info: t(q.brand.register_info ?? null),
          represented_by: t(q.representedBy ?? null),
          contact_name: t(q.contact?.name),
          contact_email: t(q.contact?.email),
        }
      : {},
    deal: q.deal
      ? {
          title: t(q.deal.title),
          budget: q.deal.budget_cents,
          currency: q.deal.currency,
          commission_percent: commissionBp != null ? commissionBp / 100 : null,
          talent_share: talentShare,
          agency_fee: fee,
          model: q.deal.model ? DEAL_MODEL_LABEL[q.deal.model]?.[lang] ?? q.deal.model : null,
          is_barter: barter,
          is_commission_only: q.deal.model === "commission_only",
          is_recurring: q.deal.model === "monthly" || q.deal.model === "installments",
          items: itemsText,
          extras: barter ? null : itemsText,
          consideration: barter ? itemsText : null,
          items_total: items.length ? itemsTotal : null,
          total: q.deal.budget_cents != null || items.length ? (q.deal.budget_cents ?? 0) + itemsTotal : null,
          monthly_schedule: schedule,
          start_month: start,
          end_month: start && months ? addMonths(start, months - 1) : start,
          months,
          deliverables: t(q.deal.deliverables),
          // 0068: Beiträge mit Fristen; ohne angelegte Beiträge der Freitext der Leistungen
          deliverables_list: deliverablesListOrText(q.beitraege ?? [], q.deal.deliverables, { lang, locale, platformLabel }),
          platforms: platformText(q.deal.platforms),
          usage_rights: t(q.deal.usage_rights),
          // VERTRAEGE-R3-07: Nutzungsrechte aus den Beiträgen, damit § 2 und § 7 dieselbe Dauer nennen
          usage_rights_list: rights?.list ?? null,
          usage_rights_by_piece: rights?.byPiece ?? false,
          usage_rights_months: rights?.months ?? null,
          usage_rights_until: q.deal.usage_rights_until ?? null,
          content_deadline: q.deal.content_deadline ?? null,
          exclusive: q.deal.exclusive ?? null,
          exclusive_until: q.deal.exclusive ? q.deal.exclusive_until ?? null : null,
          exclusive_scope: q.deal.exclusive ? t(q.deal.exclusive_scope ?? null) : null,
          invoice_issuer: payee,
          payee,
          // CO-009: vereinbart an der Kooperation, sonst Standard der Agentur
          free_revisions: q.deal.free_revisions ?? q.org.free_revisions ?? null,
          revision_fee: q.deal.revision_fee_cents ?? q.org.revision_fee_cents ?? null,
        }
      : {},
    contract: {
      number,
      title: t(q.contract.title),
      date: q.contract.date,
      kind: q.contract.kind,
      language: lang,
      starts_on: q.contract.starts_on ?? null,
      ends_on: q.contract.ends_on ?? null,
      term_months: q.contract.term_months ?? null,
      auto_renew: q.contract.auto_renew ?? false,
      renewal_months: q.contract.auto_renew ? q.contract.renewal_months ?? 12 : q.contract.renewal_months ?? null,
      notice_days: q.contract.notice_days ?? null,
      notice_period: noticePeriodText(q.contract.notice_days ?? null, lang),
      four_eyes: (q.contract.agency_signatures ?? 1) > 1,
      exclusive: q.contract.exclusive_management ?? false,
      exclusive_scope: t(q.contract.exclusive_scope ?? null),
      tail_months: q.contract.tail_months ?? null,
    },
    original: {
      number: q.original?.number ?? null,
      title: q.original ? t(q.original.title) : null,
      date: q.original?.date ?? null,
      reference: q.original ? [q.original.number ?? q.original.title, originalDate ? (lang === "en" ? `dated ${originalDate}` : `vom ${originalDate}`) : null].filter(Boolean).join(" ") : null,
    },
    dates: { today: q.today, year: Number(q.today.slice(0, 4)) },
  };
}

// ── Quellen aus geladenen Datensätzen ────────────────────────────────────────────

import type { BrandFull, ContactRow, DealFull, ItemRow, OrgDaten, PeriodRow, TalentFull } from "./daten";

/** Ansprechperson der Marke für den Vertragstext: Rechnungsempfänger zuerst, sonst die erste Person mit Adresse. */
export function primaryContact(contacts: readonly ContactRow[]): ContactRow | null {
  return contacts.find((c) => c.is_invoice_recipient && c.email) ?? contacts.find((c) => c.email) ?? contacts[0] ?? null;
}

const SIGNING_ROLE = /geschäftsf|geschaeftsf|inhaber|owner|ceo|managing|prokur|vorstand|legal|recht|justiz|director/i;

/**
 * Unterzeichner der Marke vorbelegen (Befund 18): wer als „unterschreibt Verträge“ markiert ist, sonst eine Person mit
 * zeichnungsberechtigter Funktion, sonst die Ansprechperson.
 */
export function signingContacts(contacts: readonly ContactRow[]): ContactRow[] {
  const withMail = contacts.filter((c) => c.email);
  const marked = withMail.filter((c) => c.signs_contracts);
  if (marked.length) return marked;
  const byRole = withMail.filter((c) => SIGNING_ROLE.test(c.role ?? ""));
  if (byRole.length) return [byRole[0]!];
  const p = primaryContact(contacts);
  return p?.email ? [p] : [];
}

export function quellen(p: {
  orgName: string;
  org: OrgDaten;
  talent: TalentFull | null;
  brand: BrandFull | null;
  representedBy?: string | null;
  contacts: readonly ContactRow[];
  deal: DealFull | null;
  items?: readonly ItemRow[];
  periods?: readonly PeriodRow[];
  beitraege?: KontextQuellen["beitraege"];
  plattformen?: KontextQuellen["plattformen"];
  terms: TermsQuelle | null;
  contract: KontextQuellen["contract"];
  guardians?: readonly string[] | null;
  original?: KontextQuellen["original"];
  today: string;
}): KontextQuellen {
  const s = p.org.settings;
  const contact = primaryContact(p.contacts);
  return {
    org: {
      name: p.orgName, legal_name: s.legal_name, street: s.street, postal_code: s.postal_code, city: s.city, country: s.country,
      vat_id: s.vat_id, tax_number: s.tax_number, reply_to_email: s.reply_to_email, default_commission_bp: s.default_commission_bp,
      agency_fee_mode: (s["agency_fee_mode"] as string | null) ?? null, agency_fee_bp: (s["agency_fee_bp"] as number | null) ?? null,
      agency_fee_cents: (s["agency_fee_cents"] as number | null) ?? null, locale: s.locale, invoice_route: s.invoice_route ?? null,
      payment_terms_days: s.payment_terms_days ?? null, talent_payment_terms_days: s.talent_payment_terms_days ?? null,
      brand_payment_terms_days: s.brand_payment_terms_days ?? null,
      talent_payout_mode: (s["talent_payout_mode"] as string | null) ?? null,
      free_revisions: (s["free_revisions"] as number | null) ?? null, revision_fee_cents: (s["revision_fee_cents"] as number | null) ?? null,
    },
    legal: p.org.legal,
    legalEn: p.org.contractSettings,
    talent: p.talent,
    brand: p.brand,
    representedBy: p.representedBy ?? null,
    contact: contact ? { name: contact.name, email: contact.email } : null,
    deal: p.deal,
    items: p.items ?? [],
    periods: p.periods ?? [],
    beitraege: p.beitraege ?? [],
    plattformen: p.plattformen ?? [],
    terms: p.terms,
    contract: p.contract,
    guardians: p.guardians ?? null,
    original: p.original ?? null,
    today: p.today,
  };
}
